import React, { useEffect, useMemo, useState } from "react";
import "../styles/CompanyCards.css";
import SecurityIcon from "../icons/BusinessHub/SecurityIcon";
import EnforcedIcon from "../icons/BusinessHub/EnforecedMultiFactorIcon";
import GloablButton from "./GloablButton";
import ExclamationIcon from "../icons/BusinessHub/exclamationRedIcon";
import PointRedIcon from "../icons/BusinessHub/PointRedIcon";
import UserIcon from "../icons/BusinessHub/UserIcon";
import CubeIcon from "../icons/BusinessHub/CubeIcon";
import LastActivityIcon from "../icons/BusinessHub/LastActivityIcon";
import { userService } from "../services/userService";
import companyController from "../controllers/companyController.js";
import userController from "../controllers/userController";
import Utils from "../utils/Utils";
import { EncryptStorage } from "encrypt-storage";

function CompanyCards() {
  const [companies, setCompanies] = useState([]);

  const [enableModal, setEnableModal] = useState({ open: false, index: null });
  const [disableModal, setDisableModal] = useState({
    open: false,
    index: null,
  });

  const [otp, setOtp] = useState("");
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [sessionUser, setSessionUser] = useState(null);

  const encryptStorage2 = useMemo(
    () => new EncryptStorage("secret-key1", { prefix: "@instance2" }),
    [],
  );

  const closeEnable = () => setEnableModal({ open: false, index: null });
  const closeDisable = () => setDisableModal({ open: false, index: null });

  const getToday = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const safeParseJwt = (token) => {
    if (!token || typeof token !== "string") return null;
    try {
      return Utils.parseJwt(token);
    } catch {
      return null;
    }
  };

  const readJwtFromStorage = () => {
    const jwtKey = encryptStorage2.getItem("user-data");
    if (jwtKey && typeof jwtKey === "string") return jwtKey;

    const arr = encryptStorage2.getItem("user-data");
    const first = Array.isArray(arr) ? arr[0] : null;

    if (typeof first === "string") return first;

    const nested = first?.jwt || first?.token || "";
    return typeof nested === "string" ? nested : "";
  };

  const getJwt = () => {
    const direct =
      typeof userService?.getUser === "function" ? userService.getUser() : "";
    if (direct && typeof direct === "string") return direct;

    const u =
      typeof userService?.getUser === "function" ? userService.getUser() : null;
    const jwtFromUser = typeof u === "string" ? u : u?.jwt || u?.token || "";
    if (jwtFromUser && typeof jwtFromUser === "string") return jwtFromUser;

    return readJwtFromStorage();
  };

  const getUserIdFromJwt = (jwt) => {
    const parsed = safeParseJwt(jwt);
    return Utils.getUserIdFromJwt(parsed);
  };

  const mapCompanyRow = (company = {}) => {
    const mfaValue =
      company?.isMfaEnabled ??
      company?.enforcedMfa ??
      company?.isEnforcedMfa ??
      company?.enforcedMFA ??
      false;

    return {
      companyName: company?.companyName || company?.name || "Company",
      companyID: String(company?.companyID || company?.companyId || "").trim(),
      users: Number(company?.users || company?.usersCount || 0),
      inventory: Number(company?.inventory || company?.inventoryCount || 0),
      last:
        company?.last ||
        company?.lastActivity ||
        company?.lastActivityDate ||
        "-",
      mfa: Boolean(mfaValue),
      enabledOn: company?.enabledOn || company?.mfaEnabledOn || "",
    };
  };

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const jwt = getJwt();
        if (!jwt) return;

        const userId = getUserIdFromJwt(jwt);
        if (!userId) return;

        const primaryRes = await companyController.userPrimaryCompanies({
          Authorization: jwt,
          userId,
        });

        const source = Array.isArray(primaryRes?.data) ? primaryRes.data : [];
        if (!source.length) {
          setCompanies([]);
          return;
        }

        const baseCompanies = source.map((item) => mapCompanyRow(item));
        const withUsers = await Promise.all(
          baseCompanies.map(async (company) => {
            try {
              const empRes = await companyController.getCompanyEmployees({
                companyId: company.companyID,
                page: 1,
                size: 1,
                jwt,
                search: "",
              });

              return {
                ...company,
                users: Number(empRes?.data?.totalElements || 0),
              };
            } catch {
              return company;
            }
          }),
        );
        setCompanies(withUsers);
      } catch {
        setCompanies([]);
      }
    };

    loadCompanies();
  }, []);

  const extractEmailFromUser = (u) =>
    u?.email || u?.user?.email || u?.data?.email || u?.userEmail || "";

  const ensureSessionUser = async () => {
    if (sessionUser) return sessionUser;

    const jwt = getJwt();
    if (!jwt) throw new Error("NO_JWT");

    const res = await userController.getUser({ Authorization: jwt });
    const u = res?.data || null;

    if (!u) throw new Error("NO_USER_DATA");

    setSessionUser(u);
    return u;
  };

  const resolveEmail = async () => {
    const jwt = getJwt();
    if (!jwt) throw new Error("NO_JWT");

    const u = await ensureSessionUser();
    const email = extractEmailFromUser(u);
    if (!email) throw new Error("NO_EMAIL");

    return { jwt, email };
  };

  const resolveCompanyID = (idx) => {
    const row = companies?.[idx]?.companyID;
    if (row && String(row).trim()) return String(row).trim();
    return "";
  };

  const openOtpFlow = async (index) => {
    try {
      setErrorMsg("");
      setOtp("");

      const companyID = resolveCompanyID(index);
      if (!companyID) {
        setErrorMsg("NO_COMPANY_ID");
        return;
      }

      setCompanies((prev) =>
        prev.map((c, i) => (i === index ? { ...c, companyID } : c)),
      );

      setLoadingOtp(true);

      const { email } = await resolveEmail();
      if (!email || !String(email).includes("@")) {
        setErrorMsg("BAD_EMAIL");
        return;
      }

      const otpEmailSubject = companies[index].mfa
        ? "Disable Enforced MFA OTP"
        : "Enable Enforced MFA OTP";

      const sendOtp = await userController.sendEmailOtp({
        email,
        otpEmailSubject,
      });

      if (Utils.isSuccessResponse(sendOtp)) {
        if (companies[index].mfa) setDisableModal({ open: true, index });
        else setEnableModal({ open: true, index });
        return;
      }

      setErrorMsg(sendOtp?.data?.message || "SEND_OTP_FAILED");
    } catch (e) {
      setErrorMsg(String(e?.message || "OTP_FLOW_ERROR"));
    } finally {
      setLoadingOtp(false);
    }
  };

  const verifyOtp = async () => {
    try {
      const { email } = await resolveEmail();
      const verifyOtpDTO = { email, otp: otp.trim() };
      const res = await userController.verifyEmailOtp({ verifyOtpDTO });

      const ok = Utils.isSuccessResponse(res);
      const msg = String(res?.data?.message || "");
      const verified =
        ok &&
        (msg === "OTP Verified" || msg === "Verified" || res?.data === true);

      if (!verified) setErrorMsg(msg || "OTP_INVALID");
      return verified;
    } catch (e) {
      setErrorMsg(String(e?.message || "OTP_VERIFY_ERROR"));
      return false;
    }
  };

  const applyToggleToUI = (idx, nextMfaValue) => {
    setCompanies((prev) =>
      prev.map((c, i) =>
        i === idx
          ? {
              ...c,
              mfa: nextMfaValue,
              enabledOn: nextMfaValue ? getToday() : "",
            }
          : c,
      ),
    );
  };

  const doToggleBackend = async (companyID) => {
    const { jwt } = await resolveEmail();
    if (!companyID) throw new Error("NO_COMPANY_ID");

    const res = await companyController.toggleEnfroceMFA({
      companyID,
      jwt,
    });

    if (!Utils.isSuccessResponse(res))
      throw new Error(res?.data?.message || "TOGGLE_FAILED");

    const v = res?.data;
    if (typeof v === "boolean") return v;

    const v2 = res?.data?.data;
    if (typeof v2 === "boolean") return v2;

    throw new Error("BAD_TOGGLE_RESPONSE");
  };

  const confirmEnable = async () => {
    const idx = enableModal.index;
    if (idx === null) return;
    if (!otp.trim()) return;

    try {
      setLoadingConfirm(true);
      setErrorMsg("");

      const companyID = resolveCompanyID(idx);
      if (!companyID) {
        setErrorMsg("NO_COMPANY_ID");
        return;
      }

      const ok = await verifyOtp();
      if (!ok) return;

      const newValue = await doToggleBackend(companyID);
      applyToggleToUI(idx, newValue);

      setOtp("");
      closeEnable();
      closeDisable();
    } catch (e) {
      setErrorMsg(String(e?.message || "ENABLE_FAILED"));
    } finally {
      setLoadingConfirm(false);
    }
  };

  const confirmDisable = async () => {
    const idx = disableModal.index;
    if (idx === null) return;
    if (!otp.trim()) return;

    try {
      setLoadingConfirm(true);
      setErrorMsg("");

      const companyID = resolveCompanyID(idx);
      if (!companyID) {
        setErrorMsg("NO_COMPANY_ID");
        return;
      }

      const ok = await verifyOtp();
      if (!ok) return;

      const newValue = await doToggleBackend(companyID);
      applyToggleToUI(idx, newValue);

      setOtp("");
      closeEnable();
      closeDisable();
    } catch (e) {
      setErrorMsg(String(e?.message || "DISABLE_FAILED"));
    } finally {
      setLoadingConfirm(false);
    }
  };

  const prettyError = (msg) => {
    if (msg === "NO_COMPANY_ID") return "No Company ID found in session.";
    if (msg === "NO_JWT") return "Session expired. Please sign in again.";
    if (msg === "NO_USER_ID") return "Session invalid. Please sign in again.";
    if (msg === "NO_EMAIL") return "Email missing. Please sign in again.";
    return msg;
  };

  return (
    <>
      <div className="company-setting-cards-grid">
        {companies.map((c, i) => (
          <div className="company-setting-card" key={i}>
            <div className="company-setting-card-header">
              <div className="company-setting-card-title">
                {c.companyName || "Company"}
              </div>
              <div className="company-setting-card-subtitle">
                Company ID: {c.companyID || "xxxx"}
              </div>
            </div>

            <div className="company-setting-stats">
              <div className="company-setting-stat">
                <div className="company-setting-stat-label">
                  <UserIcon /> Users
                </div>
                <div className="company-setting-stat-value">{c.users}</div>
              </div>

              <div className="company-setting-stat">
                <div className="company-setting-stat-label">
                  <CubeIcon /> Inventory
                </div>
                <div className="company-setting-stat-value">{c.inventory}</div>
              </div>

              <div className="company-setting-stat">
                <div className="company-setting-stat-label">
                  <LastActivityIcon /> Last Activity
                </div>
                <div className="company-setting-stat-value">{c.last}</div>
              </div>
            </div>

            <div className="company-setting-box">
              <div className="company-setting-sec-title">
                <SecurityIcon /> Security Settings
              </div>

              <div className="company-setting-sec-box">
                <div className="company-setting-sec-top">
                  <div className="company-setting-sec-name">
                    <EnforcedIcon />
                    <span>Enforced Multi-Factor Authentication</span>
                  </div>

                  <div
                    className={
                      c.mfa
                        ? "company-setting-pill company-setting-pill-enabled"
                        : "company-setting-pill company-setting-pill-disabled"
                    }
                  >
                    {c.mfa ? "Enabled" : "Disabled"}
                  </div>
                </div>

                <div className="company-setting-sec-desc">
                  Require all users in this company to sign in using
                  multi-factor authentication.
                </div>

                {c.mfa && (
                  <div className="company-setting-sec-meta">
                    Enabled On: {c.enabledOn}
                  </div>
                )}

                <div className="company-setting-sec-footer">
                  <div className="company-setting-sec-footer-text">
                    {c.mfa ? "Disable Enforced MFA" : "Enable Enforced MFA"}
                  </div>

                  <label
                    className="company-setting-switch"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (loadingOtp || loadingConfirm) return;
                      openOtpFlow(i);
                    }}
                  >
                    <input type="checkbox" checked={c.mfa} readOnly />
                    <span className="company-setting-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {enableModal.open && (
        <div
          className="cs-modal-overlay"
          onClick={loadingConfirm ? undefined : closeEnable}
        >
          <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cs-modal-title">
              Enable Enforced MFA for this company?
            </div>

            <div className="cs-modal-desc">
              When enforced, all users under this company will be required to
              set up multi-factor authentication on their next login.
            </div>

            <div className="cs-modal-info cs-modal-info-blue">
              <div className="cs-modal-info-title">
                Important Considerations
              </div>
              <div className="cs-modal-info-text">
                ✓ All employees will be required to enable MFA.
              </div>
              <div className="cs-modal-info-text">
                ✓ Users without MFA will be redirected to setup.
              </div>
              <div className="cs-modal-info-text">
                ✓ Applies only to Company Name.
              </div>
            </div>

            <div className="cs-modal-otp">
              <div className="cs-modal-otp-label">Confirm with OTP</div>
              <input
                className="cs-modal-otp-input"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value);
                  if (errorMsg) setErrorMsg("");
                }}
              />
              <div className="cs-modal-otp-help">
                For security, please confirm this action with code we send you
                by email.
              </div>
              {errorMsg ? (
                <div className="cs-modal-error">{prettyError(errorMsg)}</div>
              ) : null}
            </div>

            <div className="cs-modal-actions">
              <button
                className="cs-btn cs-btn-blue"
                onClick={confirmEnable}
                disabled={loadingConfirm}
              >
                {loadingConfirm ? "Processing..." : "Confirm & Enable MFA"}
              </button>
              <button
                className="cs-btn cs-btn-outline"
                onClick={closeEnable}
                disabled={loadingConfirm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {disableModal.open && (
        <div
          className="cs-modal-overlay"
          onClick={loadingConfirm ? undefined : closeDisable}
        >
          <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cs-modal-title">Disable Enforced MFA?</div>

            <div className="cs-modal-desc">
              This will remove the MFA requirement for all users in this
              company. This may reduce account security.
            </div>

            <div className="cs-modal-info cs-modal-info-red">
              <div className="cs-modal-info-title-cs-red">
                <ExclamationIcon /> Security Impact
              </div>

              <div className="cs-modal-info-text-cs-red">
                <PointRedIcon /> Users will no longer be required to use MFA.
              </div>

              <div className="cs-modal-info-text-cs-red">
                <PointRedIcon /> Account security may be compromised.
              </div>

              <div className="cs-modal-info-text-cs-red">
                <PointRedIcon /> This action applies to all users in this
                company.
              </div>
            </div>

            <div className="cs-modal-otp">
              <div className="cs-modal-otp-label">Confirm with OTP</div>
              <input
                className="cs-modal-otp-input"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value);
                  if (errorMsg) setErrorMsg("");
                }}
              />
              <div className="cs-modal-otp-help">
                For security, please confirm this action with code we send you
                by email.
              </div>
              {errorMsg ? (
                <div className="cs-modal-error">{prettyError(errorMsg)}</div>
              ) : null}
            </div>

            <div className="cs-modal-actions-disable">
              {/* <GloablButton
                width="220px"
                height="44px"
                backgroundColor="#F44336"
                text={
                  loadingConfirm ? "Processing..." : "Confirm & Disable MFA"
                }
                borderRadius="50px"
                color="#FFFFFF"
                padding="11.0527px 20px"
                boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
                onClick={confirmDisable}
                disabled={loadingConfirm}
              /> */}
              <div
                className="confirm-disable-mfs-action-button"
                onClick={confirmDisable}
              >
                Confirm & Disable MFA
              </div>
              <button
                className="cs-btn cs-btn-outline"
                onClick={closeDisable}
                disabled={loadingConfirm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CompanyCards;
